import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { useAuthStore } from '../store/authStore';
import { useSignOut } from '../hooks/useSignOut';
import { useScrollTracking } from '../hooks/useScrollTracking';
import { formatLocalTime } from '../utils/time';
import { BottomNav } from '../components/ui/BottomNav';
import { StatusBadge } from '../components/ui/StatusBadge';
import { AvailabilityPill } from '../components/ui/AvailabilityPill';
import { AttributeChips } from '../components/ui/AttributeChips';
import { LanguagePicker } from '../components/ui/LanguagePicker';
import { useTranslation } from '../i18n';
import type { ParkingSpotAttributes } from '@lotngo/shared';

// ─── types ────────────────────────────────────────────────────────────────────

interface MySpot {
  id: string;
  spot_number: string;
  level_code: string;
  level_label: string;
  description: string | null;
  status: 'free' | 'occupied' | 'unavailable';
  attributes?: ParkingSpotAttributes;
  registered_count: number;
  max_spots: number;
  active_session: { started_at: string; occupant_apartment: string } | null;
  availability: { source: 'schedule' | 'window'; until: string } | null;
  pending_reclaim_alert: boolean;
  reclaim_alert_sent_at: string | null;
}

// ─── SpotCard ─────────────────────────────────────────────────────────────────

function SpotCard({
  spot,
  timezone,
  t,
}: {
  spot: MySpot;
  timezone: string;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  const navigate = useNavigate();

  // Each action targets its own route — share (free up) goes to /free,
  // edit (everything else: attributes, schedule, delete) goes to detail.
  // We deliberately drop the whole-card click in favour of explicit buttons
  // so the affordance for both actions is visible. stopPropagation on each
  // button keeps the parent harmless if someone re-adds a card click later.
  const goEdit  = (e: React.MouseEvent) => { e.stopPropagation(); navigate(`/spots/${spot.id}`); };
  const goShare = (e: React.MouseEvent) => { e.stopPropagation(); navigate(`/spots/${spot.id}/free`); };

  return (
    <div
      style={{
        backgroundColor: '#ffffff',
        borderRadius:    '20px',
        boxShadow:       '0 8px 24px -4px rgba(43,52,55,0.06)',
        padding:         '18px 20px',
        marginBottom:    '12px',
        userSelect:      'none',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, marginRight: '12px' }}>
          <div style={{ fontFamily: 'var(--manrope)', fontWeight: 700, fontSize: '16px', marginBottom: '4px', color: 'var(--on-surface)' }}>
            {t(spot.level_code) || spot.level_label} · {spot.spot_number}
          </div>

          {spot.availability ? (
            <div style={{ marginBottom: '4px' }}>
              <AvailabilityPill source={spot.availability.source} until={spot.availability.until} timezone={timezone} />
            </div>
          ) : (
            <div style={{ fontSize: '12px', color: 'var(--on-surface-3)', marginBottom: '4px' }}>
              {t('no_schedule_set')}
            </div>
          )}

          <AttributeChips attributes={spot.attributes} />

          {spot.status === 'occupied' && spot.active_session && (
            <div style={{ fontSize: '12px', color: '#d97706', fontWeight: 500, marginTop: '2px' }}>
              {t('apt_label')} {spot.active_session.occupant_apartment}
              {' · '}{t('since')}{' '}
              {formatLocalTime(spot.active_session.started_at, timezone, 'time')}
              {spot.pending_reclaim_alert && ` · ⚠ ${t('alert_sent')} ${spot.reclaim_alert_sent_at ? formatLocalTime(spot.reclaim_alert_sent_at, timezone, 'time') : ''}`}
            </div>
          )}
        </div>

        <StatusBadge status={spot.status} />
      </div>

      <div style={{
        display:        'flex',
        gap:            '8px',
        marginTop:      '14px',
        paddingTop:     '14px',
        borderTop:      '1px solid rgba(43,52,55,0.06)',
      }}>
        {spot.status === 'occupied' ? (
          <button onClick={goEdit} style={{ ...primaryAction, background: 'rgba(254,242,242,0.8)', color: '#dc2626' }} aria-label={t('reclaim_spot')}>
            <span className="material-symbols-outlined" style={{ fontSize: '18px' }} aria-hidden>assignment_return</span>
            {t('reclaim_spot')}
          </button>
        ) : (
          <button onClick={goShare} style={primaryAction} aria-label={t('share_availability')}>
            <span className="material-symbols-outlined" style={{ fontSize: '18px' }} aria-hidden>ios_share</span>
            {t('share_availability')}
          </button>
        )}
        <button onClick={goEdit} style={secondaryAction} aria-label={t('edit_spot')}>
          <span className="material-symbols-outlined" style={{ fontSize: '18px' }} aria-hidden>edit</span>
          {t('edit_spot')}
        </button>
      </div>
    </div>
  );
}

// ─── EmptyState ───────────────────────────────────────────────────────────────

function EmptyState({ onAdd, t }: { onAdd: () => void; t: ReturnType<typeof useTranslation>['t'] }) {
  return (
    <div style={{
      background:   'var(--surface-low)',
      borderRadius: '24px',
      padding:      '40px 24px',
      textAlign:    'center',
      boxShadow:    '0 8px 24px -4px rgba(43,52,55,0.04)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '28px' }}>
        <div style={{ position: 'relative' }}>
          <div style={{
            width:          '88px',
            height:         '88px',
            borderRadius:   '9999px',
            background:     'var(--surface-high)',
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'center',
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: '44px', color: 'var(--on-surface-3)' }}>
              location_off
            </span>
          </div>
          <div style={{
            position:       'absolute',
            bottom:         '-4px',
            right:          '-4px',
            width:          '32px',
            height:         '32px',
            borderRadius:   '9999px',
            background:     '#ffffff',
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'center',
            boxShadow:      '0 2px 8px rgba(43,52,55,0.12)',
          }}>
            <span
              className="material-symbols-outlined"
              style={{ fontSize: '20px', color: 'var(--primary)', fontVariationSettings: "'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24" }}
            >
              add_circle
            </span>
          </div>
        </div>
      </div>

      <h2 style={{ fontFamily: 'var(--manrope)', fontSize: '22px', fontWeight: 700, color: 'var(--on-surface)', marginBottom: '10px' }}>
        {t('no_spots_registered')}
      </h2>
      <p style={{ fontSize: '14px', color: 'var(--on-surface-3)', lineHeight: 1.6, marginBottom: '28px', maxWidth: '280px', marginLeft: 'auto', marginRight: 'auto' }}>
        {t('no_spots_message')}
      </p>

      <button
        onClick={onAdd}
        style={{
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'center',
          gap:            '8px',
          margin:         '0 auto',
          padding:        '14px 32px',
          borderRadius:   '12px',
          border:         'none',
          background:     'linear-gradient(135deg, #005bc0 0%, #004fa9 100%)',
          color:          '#f7f7ff',
          fontSize:       '15px',
          fontWeight:     600,
          cursor:         'pointer',
          boxShadow:      '0 4px 16px rgba(0,91,192,0.25)',
        }}
      >
        <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>add</span>
        {t('add_first_spot')}
      </button>
    </div>
  );
}

// ─── MySpotsPage ──────────────────────────────────────────────────────────────

export default function MySpotsPage() {
  useScrollTracking();
  const navigate = useNavigate();
  const timezone = useAuthStore((s) => s.user?.building.timezone ?? 'UTC');
  const signOut  = useSignOut();
  const { t }    = useTranslation();

  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['spots', 'mine'],
    queryFn:  () => apiClient.get<{ data: MySpot[] }>('/spots/mine').then((r) => r.data.data),
  });

  const spots    = data ?? [];
  const count    = spots.length;
  const maxSpots = spots[0]?.max_spots ?? 4;
  const atMax    = count >= maxSpots;

  return (
    <div className="page" style={{ maxWidth: '430px', margin: '0 auto' }}>

      {/* ── Top bar ── */}
      <header className="topbar pt-[env(safe-area-inset-top,0px)] h-[calc(64px+env(safe-area-inset-top,0px))]" style={{
        position:       'sticky',
        top:            0,
        zIndex:         50,
        //height:         '64px',
        padding:        '0 24px',
        display:        'flex',
        justifyContent: 'space-between',
        alignItems:     'center',
        boxShadow:      '0 12px 32px -4px rgba(43,52,55,0.06)',
      }}>
        <div style={{ width: '84px', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          <LanguagePicker />
        </div>
        <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none' }}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 114 26" style={{ height: '26px', width: 'auto', display: 'block' }} aria-label="Lot'N'Go">
            <text x="57" y="20" fontFamily="Manrope, system-ui, sans-serif" fontWeight="700" fontSize="20" fill="#005bc0" textAnchor="middle" letterSpacing="-0.4">{"Lot'N'Go"}</text>
          </svg>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <button onClick={() => navigate('/profile')} style={iconBtn} title={t('settings')} aria-label={t('settings')}>
            <span className="material-symbols-outlined" style={{ fontSize: '22px' }}>settings</span>
          </button>
          <button onClick={() => setShowSignOutConfirm(true)} style={iconBtn} title={t('sign_out')} aria-label={t('sign_out')}>
            <span className="material-symbols-outlined" style={{ fontSize: '22px' }}>logout</span>
          </button>
        </div>
      </header>

      <div className="page-body">

        {/* ── Page heading ── */}
        <section style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: '8px' }}>
            <h2 style={{ fontFamily: 'var(--manrope)', fontSize: '28px', fontWeight: 800, color: 'var(--on-surface)', letterSpacing: '-0.02em', margin: 0, lineHeight: 1.15 }}>
              {t('my_spots')}
            </h2>
            <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--primary)', background: 'var(--primary-container)', padding: '5px 12px', borderRadius: '9999px', whiteSpace: 'nowrap', flexShrink: 0, marginLeft: '12px' }}>
              {count} {t('of')} {maxSpots} {t('registered')}
            </span>
          </div>
          <p style={{ fontSize: '14px', color: 'var(--on-surface-3)', lineHeight: 1.6, margin: 0 }}>
            {t('manage_spots')}
          </p>
        </section>

        {/* ── Content ── */}
        {isLoading ? (
          <div style={{ textAlign: 'center', color: 'var(--on-surface-3)', padding: '48px 0' }}>{t('loading')}</div>
        ) : spots.length === 0 ? (
          <EmptyState onAdd={() => navigate('/spots/new')} t={t} />
        ) : (
          <>
            {spots.map((spot) => (
              <SpotCard
                key={spot.id}
                spot={spot}
                timezone={timezone}
                t={t}
              />
            ))}

            {/* Recent Activity link */}
            <button
              onClick={() => navigate('/spots/recent-claims')}
              style={{
                width:          '100%',
                padding:        '14px 18px',
                borderRadius:   '16px',
                border:         '1px solid rgba(43,52,55,0.08)',
                background:     '#ffffff',
                display:        'flex',
                alignItems:     'center',
                justifyContent: 'space-between',
                cursor:         'pointer',
                marginTop:      '4px',
                marginBottom:   '12px',
                boxShadow:      '0 4px 16px -4px rgba(43,52,55,0.05)',
                fontFamily:     "'Inter', system-ui, sans-serif",
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span
                  className="material-symbols-outlined"
                  style={{ color: '#005bc0', fontSize: '20px' }}
                  aria-hidden
                >
                  history
                </span>
                <span style={{ fontWeight: 600, fontSize: '14px', color: '#2b3437' }}>
                  {t('recent_claims_link')}
                </span>
              </span>
              <span style={{ color: 'var(--on-surface-3)', fontSize: '18px', lineHeight: 1 }}>›</span>
            </button>

            {!atMax && (
              <button
                onClick={() => navigate('/spots/new')}
                style={{
                  width:          '100%',
                  height:         '56px',
                  borderRadius:   '20px',
                  border:         '2px dashed #c7d5e8',
                  background:     'none',
                  color:          'var(--primary)',
                  fontSize:       '14px',
                  fontWeight:     600,
                  cursor:         'pointer',
                  marginTop:      '4px',
                  display:        'flex',
                  alignItems:     'center',
                  justifyContent: 'center',
                  gap:            '6px',
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>add</span>
                {t('register_another')}
              </button>
            )}
          </>
        )}
      </div>

      <BottomNav />

      {/* ── Sign out confirm ── */}
      {showSignOutConfirm && (
        <>
          <div onClick={() => setShowSignOutConfirm(false)} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 300 }} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 'calc(100% - 48px)', maxWidth: '320px', backgroundColor: '#ffffff', borderRadius: '20px', padding: '24px 20px 16px', zIndex: 301, boxShadow: '0 12px 32px -4px rgba(43,52,55,0.12)' }}>
            <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '6px' }}>{t('sign_out_title')}</div>
            <div className="body-md" style={{ marginBottom: '20px' }}>{t('sign_in_again')}</div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => setShowSignOutConfirm(false)} className="btn-secondary" style={{ flex: 1, height: '44px' }}>{t('cancel')}</button>
              <button onClick={() => { setShowSignOutConfirm(false); void signOut(); }} className="btn-primary" style={{ flex: 1, height: '44px' }}>{t('sign_out')}</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── styles ───────────────────────────────────────────────────────────────────

const iconBtn: React.CSSProperties = {
  background: 'none',
  border:     'none',
  cursor:     'pointer',
  color:      'var(--on-surface-3)',
  padding:    '8px',
  display:    'flex',
  alignItems: 'center',
  fontSize:   '20px',
  lineHeight: 1,
};

const primaryAction: React.CSSProperties = {
  flex:           1,
  display:        'flex',
  alignItems:     'center',
  justifyContent: 'center',
  gap:            '6px',
  height:         '40px',
  borderRadius:   '10px',
  border:         'none',
  background:     'linear-gradient(135deg, #005bc0 0%, #004fa9 100%)',
  color:          '#f7f7ff',
  fontFamily:     'var(--manrope)',
  fontWeight:     600,
  fontSize:       '13px',
  cursor:         'pointer',
};

const secondaryAction: React.CSSProperties = {
  flex:           1,
  display:        'flex',
  alignItems:     'center',
  justifyContent: 'center',
  gap:            '6px',
  height:         '40px',
  borderRadius:   '10px',
  border:         '1px solid rgba(43,52,55,0.12)',
  background:     '#ffffff',
  color:          'var(--on-surface)',
  fontFamily:     'var(--manrope)',
  fontWeight:     600,
  fontSize:       '13px',
  cursor:         'pointer',
};
